import { useAuthProvider } from "ra-core";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { IS_DEMO } from "../../env";
import { createLocalRecruitingClient } from "./localRecruitingClient";
import {
  createSupabaseRecruitingClient,
  type RecruitingSupabase,
} from "./supabaseRecruitingClient";
import type { RecruitingClient } from "./types";

const RecruitingClientContext = createContext<RecruitingClient | null>(null);

/** Tests (and any host) can inject a client; otherwise RPCs on Supabase, an in-memory client in the demo. */
export const RecruitingClientProvider = ({
  client,
  children,
}: {
  client: RecruitingClient;
  children: ReactNode;
}) => (
  <RecruitingClientContext.Provider value={client}>
    {children}
  </RecruitingClientContext.Provider>
);

// One demo store per page load, shared by the menu count and the page.
let demoClient: RecruitingClient | null = null;

export const useRecruitingClient = (): RecruitingClient => {
  const injected = useContext(RecruitingClientContext);
  const authProvider = useAuthProvider();
  return useMemo(() => {
    if (injected) return injected;
    if (IS_DEMO)
      return (demoClient ??= createLocalRecruitingClient(
        {},
        {
          isAdmin: async () =>
            (await authProvider?.getPermissions?.({})) === "admin",
        },
      ));
    return createSupabaseRecruitingClient(
      getSupabaseClient as unknown as () => RecruitingSupabase,
    );
  }, [injected, authProvider]);
};
